import React, { useState } from 'react';
import Image from 'gatsby-plugin-sanity-image';
import { StaticImage } from 'gatsby-plugin-image';
import cx from 'classnames';
import { Link } from 'gatsby';
import { motion } from 'framer-motion';
import Moment from 'react-moment';

import { BlockWrapper } from '../blockWrapper';
import { PortableTextBlock } from '@modules/portableTextBlock';
import { Arrow } from '@svg/arrow';

const arrowVars = {
  initial: {
    x: -20,
  },
  animate: {
    x: 0,
    transition: {
      duration: 0.3,
    },
  },
};

const EventBlock = ({ eventRef, layout, whiteText, mobileWhiteText }) => {
  const { title, session, settings, slug } = eventRef || {};

  const [hover, setHover] = useState(false);

  return (
    <div
      className={cx('col-start-2 col-end-14 md:absolute w-full mt-8 md:mt-0', {
        'md:col-start-9 md:col-end-14 bottom-28': layout === 'right',
        'md:col-end-7': layout === 'left' || !layout,
      })}>
      <div
        className={cx({
          'max-w-sm mx-auto': layout === 'right',
          'max-w-lg': layout === 'left' || !layout,
        })}>
        <div
          className={cx('hidden md:block text-black', {
            'md:text-white': whiteText,
            'md:text-black': !whiteText,
          })}>
          <span className='font-semibold text-base leading-base'>
            <Moment withTitle format='D MMMM YYYY - h:mm A'>
              {session[0]}
            </Moment>
          </span>
          <h1 className='text-6xl leading-6xl font-semibold'>{title}</h1>
          <p className='font-medium text-lg leading-lg pt-2 pb-4'>
            {settings.searchExcerpt}
          </p>
          <Link
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            to={`/events/${slug.current}`}
            className='text-lg leading-lg font-semibold flex flex-row justify-between items-center'>
            <span>More Info</span>{' '}
            <motion.span
              variants={arrowVars}
              initial='initial'
              animate={hover ? 'animate' : 'initial'}>
              <Arrow white={whiteText ? true : false} />
            </motion.span>
          </Link>
        </div>

        <div
          className={cx('md:hidden', {
            'md:text-white': whiteText,
            'md:text-black': !whiteText,
            'text-black': !mobileWhiteText,
            'text-white': mobileWhiteText,
          })}>
          <span className='font-semibold text-sm leading-sm'>
            <Moment withTitle format='D MMMM YYYY - h:mm A'>
              {session[0]}
            </Moment>
          </span>
          <h1 className='text-4xl leading-4xl font-medium'>{title}</h1>
          <p className='font-medium text-sm leading-sm pt-2 pb-4'>
            {settings.searchExcerpt}
          </p>
          <Link
            to={`/events/${slug.current}`}
            className='text-sm leading-sm font-semibold flex flex-row justify-between items-center font-bold'>
            <span style={{ fontWeight: '700' }}>More Info</span>{' '}
            <Arrow white={mobileWhiteText ? true : false} />
          </Link>
        </div>
      </div>
    </div>
  );
};

const ImageBanner = ({
  blockConfig,
  image,
  layout,
  text,
  whiteText,
  mobileWhiteText,
  eventRef,
  imageOverlay,
}) => {
  return (
    <BlockWrapper
      {...blockConfig}
      className='grid grid-cols-14 relative md:h-screen items-center'>
      <div className='col-start-1 col-end-15 overflow-hidden h-full'>
        {image && (
          <Image
            className='object-contain md:object-cover w-full md:h-full h-auto'
            {...image.image}
          />
        )}
        {layout === 'right' && (
          <>
            <div className='hidden md:block absolute top-0'>
              <StaticImage
                width={264}
                height={783}
                src={'../../../images/dso-vertical.png'}
                alt='DSO Logo'
              />
            </div>

            <div className='md:hidden absolute top-0'>
              <StaticImage
                width={116}
                height={344}
                src={'../../../images/dso-vertical.png'}
                alt='DSO Logo'
              />
            </div>
          </>
        )}
      </div>

      {!eventRef ? (
        <div
          className={cx(
            'col-start-2 col-end-14 md:absolute w-full mt-8 md:mt-0',
            {
              'md:col-start-9 md:col-end-14 bottom-28': layout === 'right',
              'md:col-end-7': layout === 'left' || !layout,
            },
          )}>
          <div
            className={cx({
              'max-w-sm mx-auto': layout === 'right',
              'max-w-lg': layout === 'left' || !layout,
            })}>
            <PortableTextBlock
              white={whiteText ? true : false}
              className={cx(
                'hidden md:block prose md:prose-lg md:prose-mb-0 md:prose-mt-0 relative z-20',
                {
                  'md:text-white': whiteText,
                  'md:text-black': !whiteText,
                },
              )}
              text={text}
            />

            <PortableTextBlock
              className={cx(
                'md:hidden prose md:prose-lg md:prose-mb-0 md:prose-mt-0 relative z-20',
                {
                  'md:text-white': whiteText,
                  'md:text-black': !whiteText,
                  'text-black': !mobileWhiteText,
                  'text-white': mobileWhiteText,
                },
              )}
              text={text}
            />
          </div>
        </div>
      ) : (
        <EventBlock
          eventRef={eventRef}
          whiteText={whiteText}
          layout={layout}
          mobileWhiteText={mobileWhiteText}
        />
      )}

      {imageOverlay && (
        <div className='w-full h-full bg-black bg-opacity-20 absolute top-0 z-10' />
      )}
    </BlockWrapper>
  );
};

export default ImageBanner;
